/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import DOMPurify from 'dompurify';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogStatus from 'layout/dialog/DialogStatus';
import DialogClose from 'layout/dialog/DialogClose';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import Button from '../Button';

/**
 * Common purpose Alert Dialog with title, message, 2 action buttons and close button.
 * Please try to reuse this component when possible rather than recreating custom dialogs.
 */
export default function AlertDialog({
  isOpen,
  title,
  message,
  width = 'md',
  confirmButtonMessage,
  handleConfirmButton,
  showCancelButton = false,
  cancelButtonMessage,
  handleCancelButton,
  showCloseButton = false,
  handleCloseButton,
  isImportantMessage = false,
}) {
  const { t } = useTranslation();

  const sanitizedHTML = DOMPurify.sanitize(t(message), {
    ADD_ATTR: ['target', 'rel'],
  });

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancelButton}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth={width}
      data-testid="alert-dialog"
    >
      {showCloseButton && <DialogClose data-testid="close-button" onClose={handleCloseButton} />}
      <DialogTitle id="alert-dialog-title">
        <Typography component="span" variant="h2">
          {isImportantMessage && <DialogStatus variant="warning" />}
          <Trans i18nKey={title} />
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />
      </DialogContent>
      <DialogActions>
        {showCancelButton && (
          <Button id="cancelButton" variant="contained" color="default" onClick={handleCancelButton}>
            <Trans i18nKey={cancelButtonMessage} />
          </Button>
        )}
        <Button id="confirmButton" color="primary" onClick={handleConfirmButton}>
          <Trans i18nKey={confirmButtonMessage} />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
